// src/components/resources/tools/generators/ColorPalette/ColorSwatch.tsx
'use client'

import { clsx } from 'clsx'
import { ClipboardIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline'
import type { ColorInfo } from './types'

/**
 * ColorSwatch: Helper for palette output, sorted and labeled
 * @param color - The color to display
 * @param copiedColor - The color that has been copied
 * @param onCopyColor - The function to copy the color
 * @param label - The label to display for the color
 *
 * @returns {JSX.Element} A JSX element
 */
export function ColorSwatch({
  color,
  copiedColor,
  onCopyColor,
  label,
}: {
  color: ColorInfo
  copiedColor: string | null
  onCopyColor: (color: string) => void
  label?: string
}): JSX.Element {
  return (
    <div className="group">
      <div
        className="w-full h-24 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-between p-3 cursor-pointer transition-transform hover:scale-105 relative overflow-hidden"
        style={{ backgroundColor: color.hex }}
        onClick={() => onCopyColor(color.hex)}
      >
        <div className="flex flex-col">
          <span className="text-xs px-2 py-1 bg-black bg-opacity-50 rounded text-white font-mono">
            {color.hex}
          </span>
          <span className="text-xs px-2 py-1 bg-black bg-opacity-30 rounded text-white mt-1">
            {label ?? color.name}
          </span>
        </div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          {copiedColor === color.hex ? (
            <ClipboardDocumentCheckIcon className="h-4 w-4 text-white" />
          ) : (
            <ClipboardIcon className="h-4 w-4 text-white" />
          )}
        </div>
        <div className={clsx(
          "absolute top-2 right-2 w-2 h-2 rounded-full",
          color.temperature === 'warm' ? "bg-orange-400" :
          color.temperature === 'cool' ? "bg-blue-400" : "bg-gray-400"
        )} />
      </div>
      <div className="mt-2 space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">Contrast:</span>
          <span className={clsx(
            "font-medium",
            color.contrast >= 7 ? "text-green-600" :
            color.contrast >= 4.5 ? "text-yellow-600" : "text-red-600"
          )}>
            {color.contrast.toFixed(1)}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">WCAG:</span>
          <span className={clsx(
            "font-medium",
            color.wcag === 'aaa' ? "text-green-600" :
            color.wcag === 'aa' ? "text-yellow-600" : "text-red-600"
          )}>
            {color.wcag.toUpperCase()}
          </span>
        </div>
        {color.metadata && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {color.metadata.saturationLevel} • {color.metadata.brightnessLevel}
          </div>
        )}
      </div>
    </div>
  )
}
